import { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import { Loader2, Search, Users, Wallet, CalendarDays, Briefcase, ArrowRight, UserPlus, UserMinus, Target } from "lucide-react";

const DEPT_COLOR = {
  "Housekeeping":          "#20B2AA",
  "Front Desk":            "#6366F1",
  "Maintenance":           "#FB7185",
  "Cluster Operation":     "#FFA500",
  "Cook":                  "#A78BFA",
  "F&B":                   "#fbbf24",
  "Property Management":   "#94a3b8",
  "Operations":            "#FFA500",
  "Other":                 "#cbd5e1",
};

const fmtL = (v) => v == null || isNaN(v) ? "—" : "₹" + (Number(v) / 100000).toFixed(1) + "L";
const fmtINR = (v) => v == null || isNaN(v) ? "—" : "₹" + Math.round(v).toLocaleString("en-IN");

/**
 * Property / Cluster staff dialog — opens when a Salary row or group header is clicked.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - target: { type: 'property' | 'cluster', name, locations: string[] }
 *  - monthKey: optional 'YYYY-MM' (defaults to latest)
 *  - monthLabel: human label e.g. 'Mar 2026'
 */
export default function PropertyStaffDialog({ open, onClose, target, monthKey, monthLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");

  useEffect(() => {
    if (!open || !target) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    setQ("");
    setDeptFilter("all");
    (async () => {
      try {
        const params = new URLSearchParams();
        if (target.type === "cluster") {
          params.set("locations", target.locations.join(","));
        } else {
          params.set("location", target.locations[0]);
        }
        if (monthKey) params.set("month_key", monthKey);
        const { data } = await api.get(`/mis-analysis/staff?${params.toString()}`);
        if (!cancelled) setData(data);
      } catch (e) {
        logError("staff dialog failed", e);
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, target, monthKey]);

  const filteredRoster = useMemo(() => {
    if (!data?.roster) return [];
    const term = q.trim().toLowerCase();
    return data.roster.filter((e) => {
      if (deptFilter !== "all" && e.department !== deptFilter) return false;
      if (!term) return true;
      return (
        (e.name || "").toLowerCase().includes(term) ||
        (e.designation || "").toLowerCase().includes(term) ||
        (e.emp_code || "").toLowerCase().includes(term) ||
        (e.location || "").toLowerCase().includes(term)
      );
    });
  }, [data, q, deptFilter]);

  const summary = data?.summary;
  const isCluster = data?.is_cluster_view;
  const deptOptions = useMemo(() => {
    if (!summary?.by_dept) return [];
    return Object.entries(summary.by_dept)
      .sort((a, b) => b[1].headcount - a[1].headcount)
      .map(([d, v]) => ({ key: d, count: v.headcount, ctc: v.ctc }));
  }, [summary]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-700" />
            {target?.name || "Staff"}
            {isCluster && <span className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 bg-indigo-100 border border-indigo-300 px-2 py-0.5 rounded">Cluster · {target?.locations?.length} props</span>}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-500">
            Salary roster from Wage Sheet · {monthLabel || data?.month_label}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading roster…
          </div>
        ) : !data || !summary ? (
          <div className="py-16 text-center text-rose-600">No staff data available.</div>
        ) : (
          <>
            {/* KPI strip — 6 tiles when AOP plan exists so users see plan vs actual at a glance */}
            <div className={`grid grid-cols-2 ${summary.aop_plan ? "md:grid-cols-6" : "md:grid-cols-5"} gap-2`} data-testid="staff-kpi">
              <DialogKPI icon={Users} label="Headcount" value={Math.round(summary.headcount).toString()} sub={`${summary.avg_days_present} avg days`} accent="#20B2AA" />
              <DialogKPI icon={Wallet} label="Permanent CTC" value={fmtL(summary.ctc)} sub={`${fmtINR(summary.ctc)} total`} accent="#6366F1" />
              <DialogKPI icon={Briefcase} label="MIS Salary" value={fmtL(summary.mis_salary)} sub="actual paid" accent="#FB7185" />
              {summary.aop_plan != null && (
                <DialogKPI
                  icon={Target}
                  label="AOP Plan"
                  value={fmtL(summary.aop_plan)}
                  sub={summary.aop_variance_pct == null ? "—" : `${summary.aop_variance_pct >= 0 ? "+" : ""}${summary.aop_variance_pct}% vs actual`}
                  accent={summary.aop_variance_pct > 15 ? "#FB7185" : summary.aop_variance_pct > 5 ? "#FFA500" : "#10B981"}
                />
              )}
              <DialogKPI icon={ArrowRight} label="Contractual gap"
                value={summary.contractual_gap_pct == null ? "—" : `${summary.contractual_gap_pct}%`}
                sub={summary.contractual_gap == null ? "no data" : `${fmtL(summary.contractual_gap)}`}
                accent={summary.contractual_gap_pct > 20 ? "#FB7185" : summary.contractual_gap_pct > 10 ? "#FFA500" : "#10B981"} />
              <DialogKPI icon={CalendarDays} label="₹ / Head" value={fmtINR(summary.per_head)} sub="MIS ÷ HC" accent="#FFA500" />
            </div>

            {/* Joiners / Leavers strip — ties to MoM Salary swings */}
            {summary && (summary.joiners_count > 0 || summary.leavers_count > 0) && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="staff-mom-strip">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <UserPlus className="w-3.5 h-3.5 text-emerald-700" />
                      <span className="text-[10.5px] uppercase tracking-wider font-bold text-emerald-800">Joined this month</span>
                    </div>
                    <span className="font-display font-black text-emerald-900 text-[18px] tabular-nums">{summary.joiners_count}</span>
                  </div>
                  <div className="text-[10.5px] text-emerald-700 tabular-nums mb-1.5">
                    {summary.new_hires_count} new hires
                    {summary.transfers_in_count > 0 && <span> · {summary.transfers_in_count} transfers in</span>}
                    <span> · +{fmtL(summary.joiners_ctc)} CTC added</span>
                  </div>
                  {data.joiners.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {data.joiners.slice(0, 8).map((j, i) => (
                        <div key={`j-${j.emp_code}-${i}`} className="text-[10.5px] text-emerald-900 flex items-center gap-1.5">
                          <span className="font-bold whitespace-nowrap">{j.name}</span>
                          <span className="text-emerald-700">· {j.designation}</span>
                          {j.is_new_hire && (
                            <span className="text-[9px] uppercase font-bold bg-emerald-200 text-emerald-900 px-1 rounded">new</span>
                          )}
                          <span className="text-emerald-600 tabular-nums ml-auto">{fmtINR(j.ctc)}</span>
                        </div>
                      ))}
                      {data.joiners.length > 8 && (
                        <div className="text-[10px] text-emerald-700 italic">+ {data.joiners.length - 8} more</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <UserMinus className="w-3.5 h-3.5 text-rose-700" />
                      <span className="text-[10.5px] uppercase tracking-wider font-bold text-rose-800">Left after last month</span>
                    </div>
                    <span className="font-display font-black text-rose-900 text-[18px] tabular-nums">{summary.leavers_count}</span>
                  </div>
                  <div className="text-[10.5px] text-rose-700 tabular-nums mb-1.5">
                    -{fmtL(summary.leavers_ctc)} CTC removed
                    <span className={`ml-2 font-bold ${summary.net_ctc_change >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      Net {summary.net_ctc_change >= 0 ? "+" : ""}{fmtL(summary.net_ctc_change)}
                    </span>
                  </div>
                  {data.leavers.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {data.leavers.slice(0, 8).map((l, i) => (
                        <div key={`l-${l.emp_code}-${i}`} className="text-[10.5px] text-rose-900 flex items-center gap-1.5">
                          <span className="font-bold whitespace-nowrap">{l.name}</span>
                          <span className="text-rose-700">· {l.designation}</span>
                          <span className="text-rose-600 tabular-nums ml-auto">{fmtINR(l.ctc)}</span>
                        </div>
                      ))}
                      {data.leavers.length > 8 && (
                        <div className="text-[10px] text-rose-700 italic">+ {data.leavers.length - 8} more</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cluster sub-property breakdown */}
            {isCluster && data.locations?.length > 1 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Properties in this group</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {data.locations.map((loc) => (
                    <div key={loc.location} className="bg-zinc-50 rounded-md border border-zinc-200 p-2">
                      <div className="font-bold text-[11.5px] text-zinc-800">{loc.location}</div>
                      <div className="text-[10.5px] text-zinc-600 tabular-nums">MIS {fmtL(loc.mis_salary)} · Rev {fmtL(loc.revenue)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Department breakdown */}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Department breakdown</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {deptOptions.map((d) => (
                  <button key={d.key}
                          onClick={() => setDeptFilter(deptFilter === d.key ? "all" : d.key)}
                          data-testid={`staff-dept-${d.key}`}
                          className={`text-left bg-white rounded-md border-2 p-2 transition-colors ${deptFilter === d.key ? "border-teal-500 bg-teal-50" : "border-zinc-200 hover:border-teal-300"}`}>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: DEPT_COLOR[d.key] || "#94a3b8" }} />
                      <span className="font-bold text-[11.5px] text-zinc-800">{d.key}</span>
                    </div>
                    <div className="tabular-nums text-[11px] text-zinc-700 mt-0.5">{Math.round(d.count)} ppl · {fmtL(d.ctc)}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Last 12 months history — MIS Salary actual + Wage CTC + Headcount + Contractual gap.
                Renders newest → oldest so the cursor lands on the freshest month first. */}
            {(data.monthly_history || []).length > 0 && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Last 13 months · cost history</div>
                  <div className="text-[10px] text-zinc-400">Newest first · MIS actual vs Wage CTC vs AOP plan</div>
                </div>
                <div className="border border-zinc-200 rounded-md overflow-x-auto">
                  <table className="w-full text-[11px]" data-testid="staff-history-table">
                    <thead className="bg-zinc-50 text-[9.5px] uppercase tracking-wider text-zinc-500 font-bold">
                      <tr>
                        <th className="py-2 px-2 text-left">Month</th>
                        <th className="py-2 px-2 text-right">MIS Salary</th>
                        <th className="py-2 px-2 text-right">AOP Plan</th>
                        <th className="py-2 px-2 text-right">vs AOP</th>
                        <th className="py-2 px-2 text-right">Wage CTC</th>
                        <th className="py-2 px-2 text-right">Headcount</th>
                        <th className="py-2 px-2 text-right">₹ / Head</th>
                        <th className="py-2 px-2 text-right">Contractual gap</th>
                        <th className="py-2 px-2 text-right">% of Rev</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {[...data.monthly_history].reverse().map((r) => {
                        const gapTone =
                          r.contractual_gap_pct == null ? "text-zinc-400" :
                          r.contractual_gap_pct <= 5 ? "text-emerald-700" :
                          r.contractual_gap_pct <= 15 ? "text-amber-700" : "text-rose-700";
                        const aopTone =
                          r.aop_variance_pct == null ? "text-zinc-400" :
                          r.aop_variance_pct > 15 ? "text-rose-700" :
                          r.aop_variance_pct > 5 ? "text-amber-700" :
                          r.aop_variance_pct < -10 ? "text-emerald-700" : "text-zinc-600";
                        return (
                          <tr key={r.key} className="hover:bg-zinc-50" data-testid={`staff-history-${r.key}`}>
                            <td className="px-2 py-1.5 font-medium">{r.month_label}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{fmtL(r.mis_salary)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500">{r.aop_plan == null ? "—" : fmtL(r.aop_plan)}</td>
                            <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${aopTone}`}>
                              {r.aop_variance_pct == null ? "—" : `${r.aop_variance_pct >= 0 ? "+" : ""}${r.aop_variance_pct}%`}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500">{r.wage_ctc == null ? "—" : fmtL(r.wage_ctc)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-zinc-600">{r.headcount == null ? "—" : Math.round(r.headcount)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-zinc-600">{r.per_head == null ? "—" : fmtINR(r.per_head)}</td>
                            <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${gapTone}`}>
                              {r.contractual_gap_pct == null ? "—" : `${r.contractual_gap_pct > 0 ? "+" : ""}${r.contractual_gap_pct}%`}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-zinc-600">
                              {r.share_of_revenue_pct == null ? "—" : `${r.share_of_revenue_pct}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Roster search + table */}
            <div className="mt-5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                  {deptFilter === "all" ? "All employees" : `${deptFilter} employees`} · {filteredRoster.length}
                </div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-zinc-400" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name, designation, code…"
                    className="pl-7 h-8 w-72 text-[12px]"
                    data-testid="staff-roster-search"
                  />
                </div>
              </div>
              <div className="border border-zinc-200 rounded-md overflow-y-auto max-h-[400px]">
                <table className="w-full text-[11.5px]">
                  <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200">
                    <tr className="text-left text-[9.5px] uppercase tracking-wider text-zinc-500">
                      <th className="py-2 px-2.5">Name</th>
                      <th className="py-2 px-2.5">Designation</th>
                      <th className="py-2 px-2.5">Dept</th>
                      {isCluster && <th className="py-2 px-2.5">Location</th>}
                      <th className="py-2 px-2.5 text-right">DOJ</th>
                      <th className="py-2 px-2.5 text-right">Days</th>
                      <th className="py-2 px-2.5 text-right">CTC</th>
                      <th className="py-2 px-2.5 text-right">In Hand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoster.length === 0 ? (
                      <tr><td colSpan={isCluster ? 8 : 7} className="py-6 text-center text-zinc-400">No matching employees.</td></tr>
                    ) : filteredRoster.map((e, i) => (
                      <tr key={`${e.emp_code}-${e.location || target?.locations[0]}-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="py-2 px-2.5 font-bold text-zinc-800 whitespace-nowrap">
                          {e.name}
                          {e.emp_code && <span className="ml-1.5 text-[9.5px] text-zinc-400 font-normal">{e.emp_code}</span>}
                        </td>
                        <td className="py-2 px-2.5 text-zinc-700">{e.designation || "—"}</td>
                        <td className="py-2 px-2.5">
                          <span className="inline-flex items-center gap-1 text-[10.5px]">
                            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: DEPT_COLOR[e.department] || "#94a3b8" }} />
                            {e.department}
                          </span>
                        </td>
                        {isCluster && <td className="py-2 px-2.5 text-zinc-600 text-[10.5px]">{e.location}</td>}
                        <td className="py-2 px-2.5 text-right tabular-nums text-zinc-600 text-[10.5px]">{e.doj ? e.doj.slice(0, 7) : "—"}</td>
                        <td className="py-2 px-2.5 text-right tabular-nums text-zinc-700">{e.days_present || "—"}</td>
                        <td className="py-2 px-2.5 text-right tabular-nums text-zinc-700 font-bold">{fmtINR(e.ctc)}</td>
                        <td className="py-2 px-2.5 text-right tabular-nums text-zinc-600">{fmtINR(e.in_hand)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[10px] text-zinc-500">
                Showing {filteredRoster.length} of {data.roster.length} employees · headcount is multi-location-aware (employees shared across properties are split proportionally).
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DialogKPI({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
      <div className="flex items-center gap-1.5 text-[9.5px] uppercase font-bold tracking-wider text-zinc-500">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        {label}
      </div>
      <div className="font-display font-black tabular-nums mt-1 text-zinc-900" style={{ fontSize: 17, lineHeight: 1.4, paddingBottom: 2 }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 tabular-nums">{sub}</div>}
      <div className="mt-1 h-[2px] w-5 rounded-full" style={{ background: accent }} />
    </div>
  );
}
